/**
 * CreateTaskFromEventModal — Modal zum Erstellen einer Aufgabe aus einem Kalendertermin
 * Vorausgefüllt mit Terminname, Datum, Kategorie
 * Unterstützt: Checkliste, Wiedervorlage, Priorität, Beschreibung
 */

import { useState, useCallback } from 'react';
import { X, Plus, Trash2, CheckSquare, CalendarClock, AlertCircle } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import type { ChecklistItem } from '@/lib/database.types';
import { toast } from 'sonner';

const KOOP_RED = '#C41E3A';

interface CalendarEventInfo {
  id?: string;
  title: string;
  start: string;
  end?: string;
  source: 'supabase' | 'google';
  businessUnit?: string;
  location?: string;
  description?: string;
  kategorie?: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  event: CalendarEventInfo;
  onSuccess?: () => void;
}

export default function CreateTaskFromEventModal({ open, onClose, event, onSuccess }: Props) {
  const [titel, setTitel] = useState(event.title || '');
  const [beschreibung, setBeschreibung] = useState(event.description || '');
  const [prioritaet, setPrioritaet] = useState<string>('mittel');
  const [kategorie, setKategorie] = useState(event.kategorie || event.businessUnit || '');
  const [faelligAm, setFaelligAm] = useState(
    event.start ? new Date(event.start).toISOString().split('T')[0] : ''
  );
  const [checkliste, setCheckliste] = useState<ChecklistItem[]>([]);
  const [neuerChecklistenPunkt, setNeuerChecklistenPunkt] = useState('');
  const [wiedervorlageDatum, setWiedervorlageDatum] = useState('');
  const [wiedervorlageNotiz, setWiedervorlageNotiz] = useState('');
  const [saving, setSaving] = useState(false);

  const addChecklistItem = useCallback(() => {
    if (!neuerChecklistenPunkt.trim()) return;
    setCheckliste(prev => [
      ...prev,
      { id: crypto.randomUUID(), text: neuerChecklistenPunkt.trim(), done: false }
    ]);
    setNeuerChecklistenPunkt('');
  }, [neuerChecklistenPunkt]);

  const removeChecklistItem = useCallback((id: string) => {
    setCheckliste(prev => prev.filter(item => item.id !== id));
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!titel.trim()) return;

    setSaving(true);
    try {
      const insertData: Record<string, any> = {
        titel: titel.trim(),
        beschreibung: beschreibung.trim() || null,
        prioritaet,
        kategorie: kategorie || null,
        status: 'offen',
        business_unit: event.businessUnit || 'gutachten',
        faellig_am: faelligAm || null,
        checkliste: checkliste.length > 0 ? checkliste : null,
        wiedervorlage_datum: wiedervorlageDatum || null,
        wiedervorlage_notiz: wiedervorlageNotiz.trim() || null,
      };

      // Verknüpfung mit Supabase-Termin
      if (event.source === 'supabase' && event.id) {
        insertData.termin_id = event.id;
      }

      const { error } = await supabase.from('aufgaben').insert(insertData);
      if (error) throw error;

      toast.success('Aufgabe erstellt', {
        description: `"${titel}" wurde aus dem Termin erstellt`,
      });
      onSuccess?.();
      onClose();
    } catch (err: any) {
      toast.error('Fehler beim Erstellen', { description: err.message });
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/30 backdrop-blur-sm" onClick={onClose}>
      <div
        className="bg-white w-full h-full sm:h-auto sm:rounded-2xl shadow-2xl sm:max-w-lg sm:mx-4 sm:max-h-[90vh] overflow-hidden flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgba(196,30,58,0.08)' }}>
              <CheckSquare className="w-4 h-4" style={{ color: KOOP_RED }} />
            </div>
            <div>
              <h3 className="text-sm font-bold text-gray-900">Aufgabe aus Termin erstellen</h3>
              <p className="text-[10px] text-gray-400">Verknüpft mit: {event.title}</p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-gray-100 transition-colors">
            <X className="w-4 h-4 text-gray-400" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-5 space-y-4">
          {/* Titel */}
          <div>
            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5">Titel *</label>
            <input
              type="text" value={titel} onChange={e => setTitel(e.target.value)} required
              placeholder="Aufgabenname"
              className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-red-200 focus:border-red-300 transition-all"
            />
          </div>

          {/* Datum + Priorität */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5">Fällig am</label>
              <input
                type="date" value={faelligAm} onChange={e => setFaelligAm(e.target.value)}
                className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-xs focus:outline-none focus:ring-2 focus:ring-red-200 focus:border-red-300 transition-all"
              />
            </div>
            <div>
              <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5">Priorität</label>
              <select
                value={prioritaet} onChange={e => setPrioritaet(e.target.value)}
                className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-xs focus:outline-none focus:ring-2 focus:ring-red-200 focus:border-red-300 transition-all bg-white"
              >
                <option value="niedrig">Niedrig</option>
                <option value="mittel">Mittel</option>
                <option value="hoch">Hoch</option>
                <option value="dringend">Dringend</option>
              </select>
            </div>
          </div>

          {/* Kategorie */}
          <div>
            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5">Kategorie</label>
            <input
              type="text" value={kategorie} onChange={e => setKategorie(e.target.value)}
              placeholder="z.B. Kfz Gutachten, Transport/Logistik"
              className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-red-200 focus:border-red-300 transition-all"
            />
          </div>

          {/* Beschreibung */}
          <div>
            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5">Beschreibung</label>
            <textarea
              value={beschreibung} onChange={e => setBeschreibung(e.target.value)}
              placeholder="Details zur Aufgabe..." rows={2}
              className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-red-200 focus:border-red-300 transition-all resize-none"
            />
          </div>

          {/* Checkliste */}
          <div>
            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5">
              Checkliste (Unteraufgaben)
            </label>
            {checkliste.length > 0 && (
              <div className="space-y-1.5 mb-2">
                {checkliste.map(item => (
                  <div key={item.id} className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2">
                    <CheckSquare className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                    <span className="text-xs text-gray-700 flex-1">{item.text}</span>
                    <button type="button" onClick={() => removeChecklistItem(item.id)}
                      className="w-6 h-6 rounded flex items-center justify-center hover:bg-red-50 transition-colors">
                      <Trash2 className="w-3 h-3 text-gray-400 hover:text-red-500" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="flex gap-2">
              <input
                type="text" value={neuerChecklistenPunkt}
                onChange={e => setNeuerChecklistenPunkt(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addChecklistItem(); } }}
                placeholder="Neuen Punkt hinzufügen..."
                className="flex-1 px-3 py-2 rounded-lg border border-gray-200 text-xs focus:outline-none focus:ring-2 focus:ring-red-200 transition-all"
              />
              <button type="button" onClick={addChecklistItem}
                className="px-3 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors">
                <Plus className="w-3.5 h-3.5 text-gray-500" />
              </button>
            </div>
          </div>

          {/* Wiedervorlage */}
          <div className="bg-amber-50/50 rounded-xl p-3 border border-amber-100">
            <div className="flex items-center gap-2 mb-2">
              <CalendarClock className="w-4 h-4 text-amber-600" />
              <label className="text-[10px] font-bold text-amber-700 uppercase tracking-widest">Wiedervorlage</label>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <input
                  type="date" value={wiedervorlageDatum} onChange={e => setWiedervorlageDatum(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-amber-200 text-xs focus:outline-none focus:ring-2 focus:ring-amber-200 transition-all bg-white"
                />
              </div>
              <div>
                <input
                  type="text" value={wiedervorlageNotiz} onChange={e => setWiedervorlageNotiz(e.target.value)}
                  placeholder="Notiz zur Wiedervorlage"
                  className="w-full px-3 py-2 rounded-lg border border-amber-200 text-xs focus:outline-none focus:ring-2 focus:ring-amber-200 transition-all bg-white"
                />
              </div>
            </div>
          </div>

          {/* Verknüpfungshinweis */}
          {event.source === 'supabase' && event.id && (
            <div className="flex items-center gap-2 text-[10px] text-gray-400 bg-gray-50 rounded-lg px-3 py-2">
              <AlertCircle className="w-3.5 h-3.5" />
              <span>Diese Aufgabe wird automatisch mit dem Termin verknüpft</span>
            </div>
          )}
        </form>

        {/* Footer */}
        <div className="flex gap-2 px-5 py-4 border-t border-gray-100 flex-shrink-0">
          <button type="button" onClick={onClose}
            className="flex-1 px-4 py-2.5 rounded-xl text-xs font-bold border border-gray-200 text-gray-600 hover:bg-gray-50 transition-all">
            Abbrechen
          </button>
          <button onClick={handleSubmit} disabled={saving || !titel.trim()}
            className="flex-1 px-4 py-2.5 rounded-xl text-xs font-bold text-white transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50"
            style={{ backgroundColor: KOOP_RED }}>
            {saving ? 'Speichern...' : 'Aufgabe erstellen'}
          </button>
        </div>
      </div>
    </div>
  );
}
